import { IMeetingSimple } from '../app/resource/meeting-simple/meeting-simple.types';
import { ISession } from '../app/resource/session/session.types';
import { IUser } from '../app/resource/user/user.types';
import { IReducedUsers } from '../components/pages/UserPage/UserList/types';

/**
 * Returns true if the logged-in user started the session or is a meeting administrator.
 * Used to gate live-session actions that regular attendees cannot perform.
 * @param loggedUserId Logged-in user id
 * @param session Current session, if any
 * @param meeting Meeting the session belongs to
 */
export const hasUserElevatedPrivilegesDuringSession = (
  loggedUserId: string,
  session: ISession | null,
  meeting: IMeetingSimple | null | undefined,
) => {
  return loggedUserId === session?.startedBy || meeting?.administratorIds.includes(loggedUserId);
};

/**
 * Returns true if the user is still an active member of the current workspace (not inactivated).
 * @param user User to check
 */
export const isMemberActiveInWorkspace = (user: IUser) => {
  return !user.deleted;
};

/** @deprecated Use isMemberActiveInWorkspace */
export const isMemberPartOfSystem = isMemberActiveInWorkspace;

/**
 * Returns true if the user is a system admin or an administrator of the given meeting.
 * @param user User to check
 * @param meeting Meeting to check against
 */
export const isMemberAnAdmin = (user: IUser, meeting: IMeetingSimple) => {
  return user.isAdmin === '1' || isMemberAMeetingAdmin(user, meeting);
};

/**
 * Returns true if the user is listed as an administrator of the meeting.
 * @param user User to check
 * @param meeting Meeting to check against
 */
export const isMemberAMeetingAdmin = (user: IUser, meeting: IMeetingSimple) => {
  return meeting.administratorIds.includes(user.id);
};

/**
 * Returns true if the user is a member or administrator of the meeting.
 * @param user User to check
 * @param meeting Meeting to check against
 */
export const isMemberPartOfMeeting = (user: IUser, meeting: IMeetingSimple) => {
  return meeting.shareWithIds.includes(user.id) || meeting.administratorIds.includes(user.id);
};

interface IGoalAssignableUsersParams {
  users: IUser[];
  meetings: IMeetingSimple[];
  loggedUserId: IUser['id'];
  isSuperAdmin: boolean;
  currentAssigneeId?: IUser['id'];
}

/**
 * Users that can be assigned as the owner of a goal.
 * Always includes the logged-in user and the current assignee (so an existing owner stays selectable when editing).
 * Super admins get every non-deleted user; everyone else gets members of meetings they administer.
 * @param params.users All users to consider
 * @param params.meetings Meetings used to determine admin membership
 * @param params.loggedUserId Logged-in user id
 * @param params.isSuperAdmin Whether the logged-in user is a super admin
 * @param params.currentAssigneeId Existing assignee to keep in the list when editing
 * @returns Users that may be assigned the goal
 */
export const getGoalAssignableUsers = ({
  users,
  meetings,
  loggedUserId,
  isSuperAdmin,
  currentAssigneeId,
}: IGoalAssignableUsersParams): IUser[] => {
  const alwaysIncludedIds = new Set([loggedUserId, currentAssigneeId].filter(Boolean));

  const adminMeetingMemberIds = new Set(
    meetings
      .filter((meeting) => meeting.administratorIds.includes(loggedUserId))
      .flatMap((meeting) => [...meeting.shareWithIds, ...meeting.administratorIds]),
  );

  return users.filter((user) => {
    if (alwaysIncludedIds.has(user.id)) {
      return true;
    }
    if (!isMemberActiveInWorkspace(user)) {
      return false;
    }
    if (isSuperAdmin) {
      return true;
    }
    return adminMeetingMemberIds.has(user.id);
  });
};

interface IGoalMeetingOptionsParams {
  meetings: IMeetingSimple[];
  users: IUser[];
  loggedUserId: IUser['id'];
  selectedUserId?: IUser['id'];
  isSuperAdmin: boolean;
  currentMeetingIds?: IMeetingSimple['id'][];
}

/**
 * Meetings that can be linked to a goal for the selected assignee.
 * Returns an empty list when no assignee is selected.
 * When assigning to self, all meetings the assignee belongs to are included.
 * When assigning to someone else, only meetings the logged-in user administers are included (unless they are a super admin).
 * Existing meeting ids are always kept so an edit form does not drop previously linked meetings.
 * @param params.meetings All meetings to consider
 * @param params.users Users used to resolve the selected assignee
 * @param params.loggedUserId Logged-in user id
 * @param params.selectedUserId Assignee whose meetings should be listed
 * @param params.isSuperAdmin Whether the logged-in user is a super admin
 * @param params.currentMeetingIds Meeting ids already linked to the goal when editing
 * @returns Meetings that may be linked to the goal
 */
export const getGoalMeetingOptions = ({
  meetings,
  users,
  loggedUserId,
  selectedUserId,
  isSuperAdmin,
  currentMeetingIds = [],
}: IGoalMeetingOptionsParams): IMeetingSimple[] => {
  if (!selectedUserId) {
    return [];
  }

  const selectedUser = users.find((user) => user.id === selectedUserId);
  if (!selectedUser) {
    return [];
  }

  const baseMeetings = meetings.filter((meeting) => {
    if (!isMemberPartOfMeeting(selectedUser, meeting)) {
      return false;
    }

    if (selectedUserId === loggedUserId) {
      return true;
    }

    if (isSuperAdmin) {
      return true;
    }

    return meeting.administratorIds.includes(loggedUserId);
  });

  const meetingIds = new Set(baseMeetings.map((meeting) => meeting.id));
  currentMeetingIds.forEach((meetingId) => meetingIds.add(meetingId));

  return meetings.filter((meeting) => meetingIds.has(meeting.id));
};

/**
 * Returns a regex match when `email` looks like a valid address, otherwise null.
 * Null and undefined values are treated as invalid.
 * @param email Value to validate
 */
export const validateEmail = (email: string | null | undefined) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};

/**
 * Groups users by the first letter of their first name, after sorting alphabetically
 * and filtering by a case-insensitive first-name search.
 * @param users Users to group
 * @param searchParam Substring matched against first names
 * @returns Map of first letter to matching users
 */
export const reduceUsersByFirstName = (users: IUser[], searchParam: string): IReducedUsers => {
  return users
    .sort((userA, userB) => userA.firstName.localeCompare(userB.firstName))
    .reduce((previousValue, currentUser) => {
      if (!currentUser || !currentUser.firstName.toLowerCase().includes(searchParam.toLowerCase())) {
        return { ...previousValue };
      }
      if (!previousValue[currentUser.firstName[0]]) {
        previousValue[currentUser.firstName[0]] = [];
      }
      previousValue[currentUser.firstName[0]] = [...previousValue[currentUser.firstName[0]], currentUser];
      return { ...previousValue };
    }, {} as IReducedUsers);
};
